/**
 * Calculation-transparency popover (§12.5 rule 3, PRD "Calculation Transparency").
 *
 * The payload is rendered 1:1 — formula, window, counted types, caveats. The client owns the
 * popover chrome and never the words: no string is composed, reworded or defaulted here.
 *
 * Kit-style dark surface; opens on hover AND focus (redesign § Design language), toggles on
 * click for touch, closes on Escape / blur / mouse-out. The panel is portaled to `<body>` so it
 * escapes the `.card { overflow: hidden }` island it is triggered from — which means the
 * pointer's trip from the trigger into the panel now crosses a DOM boundary and needs the
 * grace timer below to survive.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Tooltip } from '@lw/shared';
import { useAnchoredPosition } from '../lib/anchored';
import { InfoIcon } from './icons';
import { cx } from './cx';

export type TooltipPopoverProps = {
  tooltip: Tooltip;
  /** Accessible name of the trigger, e.g. "How reactions × is calculated". */
  label?: string;
  /** Custom trigger content; defaults to the small info glyph. */
  children?: ReactNode;
  /** Panel alignment relative to the trigger. */
  align?: 'center' | 'left';
  /** Appended as a final section — server-formatted extra lines, never client-composed text. */
  extra?: ReactNode;
  className?: string;
};

/** Grace period for moving the pointer off the trigger and onto the panel. */
const BRIDGE_MS = 100;

export function TooltipPopover({
  tooltip,
  label = 'How this figure is calculated',
  children,
  align = 'center',
  extra,
  className,
}: TooltipPopoverProps) {
  const [open, setOpen] = useState(false);
  /** Click pins the popover open until dismissed — hover alone never pins. */
  const [pinned, setPinned] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const bridge = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoverId = useId();
  const panelStyle = useAnchoredPosition(open, containerRef, panelRef, {
    align: align === 'left' ? 'end' : 'center',
    gapPx: 7,
  });

  const cancelClose = () => {
    if (bridge.current !== null) clearTimeout(bridge.current);
    bridge.current = null;
  };

  const show = () => {
    cancelClose();
    setOpen(true);
  };

  const scheduleClose = () => {
    if (pinned) return;
    cancelClose();
    bridge.current = setTimeout(() => setOpen(false), BRIDGE_MS);
  };

  useEffect(() => () => cancelClose(), []);

  useEffect(() => {
    if (!open) return;
    const dismiss = () => {
      cancelClose();
      setOpen(false);
      setPinned(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return;
      const target = event.target;
      if (containerRef.current?.contains(target) === true) return;
      if (panelRef.current?.contains(target) === true) return;
      dismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  return (
    <span
      className={cx('tip', className)}
      ref={containerRef}
      onMouseEnter={show}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="tip__trigger"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onFocus={show}
        onBlur={scheduleClose}
        onClick={() => {
          show();
          setPinned((previous) => !previous);
        }}
      >
        {children ?? <InfoIcon size={14} />}
      </button>
      {open
        ? createPortal(
            <span
              className="tip__panel"
              ref={panelRef}
              style={panelStyle ?? undefined}
              id={popoverId}
              role="tooltip"
              onMouseEnter={show}
              onMouseLeave={scheduleClose}
            >
              <span className="tip__section">
                <span className="tip__heading">Formula</span>
                <span> {tooltip.formula}</span>
              </span>
              {tooltip.window !== undefined ? (
                <span className="tip__section">
                  <span className="tip__heading">Window</span>
                  <span> {tooltip.window}</span>
                </span>
              ) : null}
              {tooltip.countedTypes !== undefined && tooltip.countedTypes.length > 0 ? (
                <span className="tip__section">
                  <span className="tip__heading">Counted post types</span>
                  <span className="tip__list" role="list">
                    {tooltip.countedTypes.map((type) => (
                      <span className="tip__item" role="listitem" key={type}>
                        {type}
                      </span>
                    ))}
                  </span>
                </span>
              ) : null}
              {tooltip.caveats !== undefined && tooltip.caveats.length > 0 ? (
                <span className="tip__section">
                  <span className="tip__heading">Caveats</span>
                  <span className="tip__list" role="list">
                    {tooltip.caveats.map((caveat) => (
                      <span className="tip__item" role="listitem" key={caveat}>
                        {caveat}
                      </span>
                    ))}
                  </span>
                </span>
              ) : null}
              {extra}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

export default TooltipPopover;
